import { useEffect, useState } from 'react';

import EmojiPickerSheet, { type PickerSection } from '@/components/EmojiPickerSheet';
import { reactionSections } from '@/constants/reactionCatalogue';
import { fetchReactionCatalogue } from '@/api/reactions';
import { useTranslation } from '@/utils/i18n';

/**
 * reaction-emoji-signal — the reaction flavour of the shared picker.
 *
 * Renders the EMBEDDED catalogue immediately (Capacitor: the sheet must open
 * instantly and offline — a loading state here would be a regression), then
 * refreshes from the server in the background. The server is the source of
 * truth because each emoji carries a family and a signed score, and those two
 * fields are what gets sold; a list duplicated in two repos would be a
 * measurement reference in two copies.
 */
export default function ReactionPicker({
  current,
  onPick,
  onClose,
}: {
  current: string | null;
  onPick: (emoji: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const label = (familyId: string) => t(`reactionFamilies.${familyId}`);
  const [sections, setSections] = useState<PickerSection[]>(() => reactionSections(label));

  useEffect(() => {
    let alive = true;
    void fetchReactionCatalogue().then((remote) => {
      // `null` = offline, or the contract is not deployed: the embedded copy
      // stands. Never blank the picker over a network blip.
      if (alive && remote) {
        setSections(
          remote.map((g) => ({
            id: g.family,
            icon: g.emoji,
            label: label(g.family),
            emojis: g.emojis,
          })),
        );
      }
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <EmojiPickerSheet
      sections={sections}
      current={current}
      onPick={onPick}
      onClose={onClose}
      testIdPrefix="reaction"
    />
  );
}
